import {
  NullArray,
  Cache,
  Variables,
  Resolver,
  DataFields,
  ResolveInfo,
  DataField
} from "@urql/exchange-graphcache";
import { PageInformation, PaginationPagingDirection } from "generated/graphql";
import { stringifyVariables } from "urql";

interface Page {
  __typename: string;
  edges: NullArray<string>;
  pageInfo: PageInformation;
}

type PaginationParams = {
  paging: PaginationPagingDirection;
  cursor: string;
  limit: number;
};

type Params = PaginationParams & {
  [key: string]: any;
};

const defaultPageInfo: PageInformation = {
  __typename: "PageInformation",
  endCursor: null,
  startCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
  total: 0
};

const ensureKey = (x: DataField): string | null =>
  typeof x === "string" ? x : null;

const concatEdges = (
  cache: Cache,
  leftEdges: NullArray<string>,
  rightEdges: NullArray<string>
) => {
  const ids = new Set<string>();
  for (let i = 0, l = leftEdges.length; i < l; i++) {
    const edge = leftEdges[i] as string | null;
    const node = cache.resolve(edge, "node");
    if (typeof node === "string") ids.add(node);
  }

  const newEdges = leftEdges.slice();
  for (let i = 0, l = rightEdges.length; i < l; i++) {
    const edge = rightEdges[i] as string | null;
    const node = cache.resolve(edge, "node");
    if (typeof node === "string" && !ids.has(node)) {
      ids.add(node);
      newEdges.push(edge);
    }
  }

  return newEdges;
};

/**
 * Helper function to modify pagination arguments.
 *
 * It will ensure that paging has value (by setting it to NEXT_PAGE if not present)
 * And remove cursor and limit arguments
 *
 * @param args
 * @returns
 */
const cleanPaginationArgs = (args: Variables): Variables => {
  const {
    paging = PaginationPagingDirection.NextPage,
    cursor,
    ...otherParams
  } = args.params as Params;
  return {
    ...args,
    params: {
      ...otherParams,
      paging
    }
  };
};

const compareArgs = (
  fieldArgs: Variables,
  connectionArgs: Variables
): boolean => {
  for (const key in connectionArgs) {
    if (!(key in fieldArgs)) {
      return false;
    }

    const argA = fieldArgs[key];
    const argB = connectionArgs[key];

    if (
      typeof argA !== typeof argB || typeof argA !== "object"
        ? argA !== argB
        : stringifyVariables(argA) !== stringifyVariables(argB)
    ) {
      return false;
    }
  }

  for (const key in fieldArgs) {
    if (!(key in connectionArgs)) return false;
  }

  return true;
};

const getPage = (
  cache: Cache,
  entityKey: string,
  fieldKey: string
): Page | null => {
  const link = ensureKey(cache.resolve(entityKey, fieldKey));
  if (!link) return null;

  const typename = cache.resolve(link, "__typename") as string;
  const edges = (cache.resolve(link, "edges") || []) as NullArray<string>;
  if (typeof typename !== "string") {
    return null;
  }

  const page: Page = {
    __typename: typename,
    edges,
    pageInfo: defaultPageInfo
  };

  const pageInfoKey = cache.resolve(link, "pageInfo");
  if (typeof pageInfoKey === "string") {
    const endCursor = ensureKey(cache.resolve(pageInfoKey, "endCursor"));
    const startCursor = ensureKey(cache.resolve(pageInfoKey, "startCursor"));
    const hasNextPage = cache.resolve(pageInfoKey, "hasNextPage");
    const hasPreviousPage = cache.resolve(pageInfoKey, "hasPreviousPage");
    const total = cache.resolve(pageInfoKey, "total");

    const pageInfo: PageInformation = (page.pageInfo = {
      __typename: "PageInformation",
      hasNextPage: typeof hasNextPage === "boolean" ? hasNextPage : !!endCursor,
      hasPreviousPage:
        typeof hasPreviousPage === "boolean" ? hasPreviousPage : !!startCursor,
      endCursor,
      startCursor,
      total: typeof total === "number" ? total : 0
    });

    if (pageInfo.endCursor === null) {
      const edge = edges[edges.length - 1] as string | null;
      if (edge) {
        const endCursor = cache.resolve(edge, "cursor");
        pageInfo.endCursor = ensureKey(endCursor);
      }
    }

    if (pageInfo.startCursor === null) {
      const edge = edges[0] as string | null;
      if (edge) {
        const startCursor = cache.resolve(edge, "cursor");
        pageInfo.startCursor = ensureKey(startCursor);
      }
    }
  }

  return page;
};

/**
 * Support for infinite scrolling for cursor-based pagination style of our APIs
 * IMPORTANT:
 * Infinite scrolling is supported for NEXT_PAGE/PREVIOUS_PAGE only,
 * for FIRST_PAGE/LAST_PAGE will return single page from cache
 */
export const cursorBasedPagination = (): Resolver => {
  return (
    _parent: DataFields,
    fieldArgs: Variables,
    cache: Cache,
    info: ResolveInfo
  ) => {
    const { parentKey: entityKey, fieldName } = info;
    let paging: PaginationPagingDirection | null = null;
    let cursor: string | null = null;

    // Paging parameter can have value of NEXT_PAGE/PREVIOUS_PAGE/FIRST_PAGE/LAST_PAGE
    // If not present, assume NEXT_PAGE by default
    if (fieldArgs.params) {
      ({
        paging = PaginationPagingDirection.NextPage,
        cursor
      } = fieldArgs.params as PaginationParams);
    }

    // Infinite scrolling is not supported if:
    // 1. There is no cursor (empty string is allowed though)
    // 2. Paging argument is not one of NEXT_PAGE or PREVIOUS_PAGE
    // Return whatever is in cache
    if (
      cursor == null ||
      (paging !== PaginationPagingDirection.NextPage &&
        paging !== PaginationPagingDirection.PreviousPage)
    ) {
      return cache.resolve(entityKey, fieldName, fieldArgs);
    }

    const allFields = cache.inspectFields(entityKey);
    const fieldInfos = allFields.filter(info => {
      return info.fieldName === fieldName;
    });
    const size = fieldInfos.length;
    if (size === 0) {
      return undefined;
    }

    let typename: string | null = null;
    let edges: NullArray<string> = [];
    let pageInfo: PageInformation = { ...defaultPageInfo };

    for (let i = 0; i < size; i++) {
      const { fieldKey, arguments: args } = fieldInfos[i];
      // Compare arguments to match cache records, with following rules:
      // 1. If paging argument is not present, assume NEXT_PAGE by default
      // 2. Ignore cursor argument, because it will change for each page request
      if (
        args === null ||
        !args.params ||
        !compareArgs(cleanPaginationArgs(fieldArgs), cleanPaginationArgs(args))
      ) {
        continue;
      }

      const page = getPage(cache, entityKey, fieldKey);
      if (page === null) {
        continue;
      }

      const {
        paging = PaginationPagingDirection.NextPage
      } = args.params as PaginationParams;

      if (paging === PaginationPagingDirection.NextPage) {
        // merge new edges at the end
        edges = concatEdges(cache, edges, page.edges);
        pageInfo.endCursor = page.pageInfo.endCursor;
        pageInfo.hasNextPage = page.pageInfo.hasNextPage;
      } else if (paging === PaginationPagingDirection.PreviousPage) {
        // Although supported, at the moment PREVIOUS_PAGE is not used anywhere on the UI

        // merge new edges at the start
        edges = concatEdges(cache, page.edges, edges);
        pageInfo.startCursor = page.pageInfo.startCursor;
        pageInfo.hasPreviousPage = page.pageInfo.hasPreviousPage;
      }

      if (typename !== page.__typename) {
        typename = page.__typename;
      }
    }

    if (typeof typename !== "string") {
      return undefined;
    }

    const hasCurrentPage = !!ensureKey(
      cache.resolve(entityKey, fieldName, fieldArgs)
    );
    if (!hasCurrentPage) {
      if (!(info as any).store.schema) {
        return undefined;
      } else {
        info.partial = true;
      }
    }

    return {
      __typename: typename,
      edges,
      pageInfo
    };
  };
};
